import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Gift, Copy, Share2, Mail, MessageCircle, Twitter, Loader2, Sparkles } from "lucide-react";
import api from "@/utils/api";

const ReferAndEarnPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/referrals/my');
        setData(res.data);
      } catch (e) {
        toast.error('Could not load your referral info');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const copyLink = () => {
    navigator.clipboard.writeText(data.referral_link);
    toast.success('Link copied to clipboard');
  };
  const copyCode = () => {
    navigator.clipboard.writeText(data.referral_code);
    toast.success('Code copied');
  };

  const shareText = data
    ? `Hey! I'm on BidZuu — use my code ${data.referral_code} to get $${data.first_order_discount} OFF your first order. You'll also earn me $${data.credit_per_referral} after your first purchase. ${data.referral_link}`
    : '';

  const shareLinks = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText)}`,
    email: `mailto:?subject=${encodeURIComponent('Join me on BidZuu — get $5 off!')}&body=${encodeURIComponent(shareText)}`,
    sms: `sms:?&body=${encodeURIComponent(shareText)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`,
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-52 pb-16 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="min-h-screen pt-44 pb-16 bg-gradient-to-br from-emerald-50 via-white to-amber-50">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl" data-testid="refer-earn-page">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-wider mb-4">
            <Sparkles className="h-3.5 w-3.5" /> Refer & Earn
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight">
            Give <span className="text-emerald-600">${data.first_order_discount}</span>,
            <br className="md:hidden" /> Get <span className="text-emerald-600">${data.credit_per_referral}</span>
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            Share your link. Friends save ${data.first_order_discount} on their first order. You earn ${data.credit_per_referral} in store credit after they buy.
          </p>
        </div>

        {/* Referral Link Card */}
        <Card className="border-emerald-200 shadow-sm mb-6">
          <CardContent className="p-6">
            <div className="space-y-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Your referral link</p>
                <div className="flex items-stretch gap-2">
                  <div className="flex-1 min-w-0 px-3 py-3 rounded-lg bg-slate-50 border border-slate-200 text-sm font-mono text-slate-700 truncate">
                    {data.referral_link}
                  </div>
                  <Button onClick={copyLink} className="bg-emerald-500 hover:bg-emerald-600 shrink-0" data-testid="copy-referral-link-btn">
                    <Copy className="h-4 w-4 mr-1" /> Copy
                  </Button>
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Or share your code</p>
                <button
                  onClick={copyCode}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-50 border border-amber-200 hover:bg-amber-100 transition font-mono text-2xl font-bold text-slate-900 tracking-widest"
                  data-testid="copy-referral-code-btn"
                >
                  {data.referral_code} <Copy className="h-4 w-4 text-slate-500" />
                </button>
              </div>
              {/* Share buttons */}
              <div className="pt-2">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Share with friends</p>
                <div className="flex flex-wrap gap-2">
                  <a href={shareLinks.whatsapp} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#25D366] text-white text-sm hover:opacity-90" data-testid="share-whatsapp">
                    <MessageCircle className="h-4 w-4" /> WhatsApp
                  </a>
                  <a href={shareLinks.email} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700 text-white text-sm hover:bg-slate-800" data-testid="share-email">
                    <Mail className="h-4 w-4" /> Email
                  </a>
                  <a href={shareLinks.sms} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-500 text-white text-sm hover:bg-blue-600" data-testid="share-sms">
                    <Share2 className="h-4 w-4" /> SMS
                  </a>
                  <a href={shareLinks.twitter} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-black text-white text-sm hover:opacity-90" data-testid="share-twitter">
                    <Twitter className="h-4 w-4" /> X (Twitter)
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <StatCard label="Invited" value={data.invited_count} testId="stat-invited" />
          <StatCard label="Purchased" value={data.completed_count} testId="stat-completed" />
          <StatCard label="Pending" value={data.pending_count} testId="stat-pending" />
          <StatCard label="Store Credit" value={`$${data.credit_balance.toFixed(2)}`} highlight testId="stat-balance" />
        </div>

        {/* How it works */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">How it works</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3 text-sm">
              <Step n="1" t="Share your link or code">
                Send your unique link to friends via WhatsApp, SMS, email, or social media.
              </Step>
              <Step n="2" t="They sign up & shop">
                Your friend signs up using your code and automatically gets ${data.first_order_discount} OFF their first order.
              </Step>
              <Step n="3" t={`You both earn $${data.credit_per_referral}`}>
                After their first purchase completes, ${data.credit_per_referral} store credit lands in BOTH your wallets. Credit expires after 14 days, so use it soon!
              </Step>
            </ol>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, highlight, testId }) => (
  <div className={`rounded-xl p-4 border ${highlight ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`} data-testid={testId}>
    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${highlight ? 'text-emerald-700' : 'text-slate-900'}`}>{value}</p>
  </div>
);

const Step = ({ n, t, children }) => (
  <li className="flex gap-3">
    <span className="h-7 w-7 flex-shrink-0 rounded-full bg-emerald-500 text-white font-bold flex items-center justify-center text-sm">{n}</span>
    <div>
      <p className="font-semibold text-slate-900">{t}</p>
      <p className="text-slate-600">{children}</p>
    </div>
  </li>
);

export default ReferAndEarnPage;
